import { getRuntimeBool, getRuntimeValue } from './runtime-config'

/**
 * Read a runtime config key as a string. Returns `''` when missing — the
 * prior `import.meta.env.VITE_*` access typed every key as `string` and
 * consumers (e.g. `createAuthBaseQuery`, `Amplify.configure`) treat empty
 * string as "unset" via existing falsy checks. Preserves that contract.
 */
function str(key: string): string {
  return getRuntimeValue(key) ?? ''
}

/** API base URLs — sourced from runtime config (with Vite env fallback). */
export const API = {
  users: str('VITE_EDGE_API_USERS'),
  data: str('VITE_EDGE_API_DATA'),
  proxy: str('VITE_EDGE_API_PRX'),
} as const

/** AWS Cognito configuration. */
export const COGNITO = {
  userPoolId: str('VITE_COGNITO_USER_POOL_ID'),
  clientId: str('VITE_COGNITO_APP_CLIENT_ID'),
} as const

/**
 * Free-trial length shown in billing copy. Display only: the length Stripe applies
 * is TRIAL_PERIOD_DAYS in edge-user-api (billing_config.py). Keep the two equal.
 */
export const TRIAL_PERIOD_DAYS = 7

/** Deployment stage — 'local' | 'staging' | 'beta' | 'production'. */
export const STAGE = str('VITE_STAGE')

/**
 * Beta preview deployment. Gates route/UI surfaces only (no self-serve billing,
 * beta-locked admission). Entitlement comes from the user's real subscription,
 * not this flag. Derived from STAGE so all gates key off one general stage value.
 */
export const BETA = STAGE === 'beta'

/**
 * This deployment is in a lazy Cognito user-migration window (beta prod→beta,
 * or staging staging→v2), so sign-in must use USER_PASSWORD_AUTH for the
 * UserMigration trigger to fire. Prod stays on SRP. Separate from BETA (which
 * gates the no-billing UI) — staging migrates but keeps normal billing.
 */
export const MIGRATION_LOGIN = getRuntimeBool('VITE_MIGRATION_LOGIN')

/**
 * Rewardful affiliate tracking. `enabled` is per-stage runtime config, because loading the
 * script points a stage at the ONE real Rewardful account — legacy ran it on local and dev
 * too, which records clicks and leads against real affiliates from developer machines.
 *
 * `scriptPath` is relative and same-origin: CloudFront proxies it to r.wdfl.co so ad
 * blockers, which filter that host by name, cannot silently drop it.
 */
export const REWARDFUL = {
  enabled: getRuntimeBool('VITE_REWARDFUL_ENABLED'),
  scriptPath: str('VITE_REWARDFUL_SCRIPT_PATH') || '/static/rw.js',
  apiKey: str('VITE_REWARDFUL_API_KEY') || '0159d3',
} as const

/** Ably channel names. */
export const ABLY_CHANNELS = {
  stockNews: str('VITE_STOCK_NEWS_ABLY_CHANNEL'),
  secFilings: str('VITE_SEC_ABLY_CHANNEL'),
  // Live chart-bar channel prefix (`<prefix>:<TICKER>`). Deployed stages share
  // `ecscharts` (separate Ably projects); a local dev running the bridge against
  // the shared staging key must override this to a unique value that matches the
  // bridge's ABLY_CHANNEL_PREFIX. See docs/chart-ably-channels.md.
  chartPrefix: str('VITE_CHART_ABLY_CHANNEL_PREFIX') || 'ecscharts',
} as const

/** Build metadata. */
export const BUILD = {
  version: str('VITE_VERSION'),
  reduxLogging: getRuntimeBool('VITE_ENABLE_REDUX_LOGGING'),
} as const

/** Default ticker symbols used across workspace pages. */
export const DEFAULT_SYMBOLS = [
  'AAPL',
  'MSFT',
  'GOOGL',
  'AMZN',
  'TSLA',
  'NVDA',
  'META',
  'NFLX',
] as const

/**
 * Unified AG Enterprise bundle key — licenses both AG Grid and AG Charts.
 * Ships in the client bundle regardless (it's a browser key, not a secret),
 * and applies to every stage, so it lives here as a plain const rather than
 * env-injected config.
 */
export const AG_GRID_LICENSE_KEY =
  'Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-119071}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Edge_to_Trade}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Edge_to_Trade}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Edge_to_Trade}_need_to_be_licensed___{Edge_to_Trade}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{26_January_2027}____[v3]_[0102]_MTgwMDkyMTYwMDAwMA==d25b8c61bc5444eefd052e5c0063f0fb'

/**
 * Python edge-llm-api. Only the TTS ticker/word clip route still lives here;
 * everything else on the AI plane is edge-companion-api (`COMPANION_API`).
 */
export const LLM_API = getRuntimeValue('VITE_EDGE_LLM_API') ?? '/api/llm'

/**
 * edge-companion-api, the TS AI plane. Analyst, deep research, knowledge/learn,
 * summaries, tools, skills, strategy AI, layout generation and panel insights
 * all hang off this host under `/v1/...`. Local dev proxies `/api/companion`
 * to :8140 (see vite.config.ts).
 */
export const COMPANION_API = getRuntimeValue('VITE_EDGE_COMPANION_API') ?? '/api/companion'

/**
 * edge-script-api, the EdgeScript service. Compiles and evaluates user
 * indicator scripts and owns My scripts, under `/v1/edgescript/...`. Local dev
 * proxies `/api/script` to :8141 (see vite.config.ts).
 */
export const SCRIPT_API = getRuntimeValue('VITE_EDGE_SCRIPT_API') ?? '/api/script'

/**
 * Chat/voice prefix for `@edge/edge-ai`, whose clients append only the leaf
 * segment (`/chat`, `/tts`, `/transcribe-stream`, `/speak-stream`).
 */
export const COMPANION_CHAT_API = `${COMPANION_API}/v1/companion`

/**
 * The admin portal's origin (scheme, host and port). Its Tools page runs a
 * browser tool in the app by postMessage, and the app takes those messages from
 * this origin only (features/companion/admin-tool-bridge.ts). Empty turns the
 * bridge off; a local dev build defaults to the admin_ui dev server.
 */
export const ADMIN_PORTAL_ORIGIN =
  getRuntimeValue('VITE_ADMIN_PORTAL_ORIGIN') ??
  (import.meta.env.DEV ? 'http://localhost:5174' : '')

/**
 * Retrostock (Nautilus single-run) sync backtest API, one ALB-fronted FastAPI
 * service (the old 3-Lambda split is gone). Local dev proxies `/api/backtest`
 * (vite.config.ts); deployed builds inject `VITE_RETROSTOCK_API`.
 */
export const RETROSTOCK_API = import.meta.env.DEV ? '/api/backtest' : str('VITE_RETROSTOCK_API')

/** PostHog analytics configuration. */
export const POSTHOG = {
  apiKey: str('VITE_POSTHOG_KEY'),
  apiHost: getRuntimeValue('VITE_POSTHOG_HOST') || 'https://us.i.posthog.com',
} as const

/**
 * Customer.io Data Pipelines JavaScript source, for web in-app messages
 * (services/customerio). Empty turns it off and the SDK is never fetched.
 */
export const CUSTOMERIO = {
  writeKey: str('VITE_CIO_CDP_WRITE_KEY'),
} as const

/**
 * Calendly booking page for the in-app "Book a demo" page (features/demo).
 * Empty hides the route and the menu entry.
 */
export const CALENDLY = {
  demoUrl: str('VITE_CALENDLY_DEMO_URL'),
} as const
